import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
} from '@stripe/react-stripe-js';
import React, { useEffect, useState } from 'react';
import './StripeCardElement.scss';

interface StripeCardElementProps {
  onCardChange?: (complete: boolean, cardName?: string, error?: any) => void;
  onCardReady?: () => void;
  onCardFocus?: () => void;
  onCardBlur?: () => void;
  checkIsCardEmpty?: (isEmpty: boolean) => void;
  className?: string;
  disabled?: boolean;
  placeholder?: string;
  style?: any;
}

const StripeCardElement: React.FC<StripeCardElementProps> = ({
  onCardChange,
  onCardReady,
  onCardFocus,
  onCardBlur,
  checkIsCardEmpty,
  className = '',
  disabled = false,
  style = {},
}) => {
  const [error, setError] = useState({
    cardNumber: null,
    cardExpiry: null,
    cardCvc: null,
  });
  const [empty, setEmpty] = useState({
    cardNumber: true,
    cardExpiry: true,
    cardCvc: true,
  });
  const [complete, setComplete] = useState({
    cardNumber: false,
    cardExpiry: false,
    cardCvc: false,
  });
  const [focused, setFocused] = useState(false);
  const [cardName, setCardName] = useState('');
  const cardElementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: '#ffffff',
        '::placeholder': {
          color: '#aab7c4',
        },
        ':-webkit-autofill': {
          color: '#ffffff',
        },
      },
      invalid: {
        color: '#ff6b6b',
      },
    },
    disabled,
    ...style,
  };

  const handleChange = (event: any) => {
    setError((prev) => ({
      ...prev,
      [event.elementType]: event.error ? event.error.message : null,
    }));
    setComplete((prev) => ({
      ...prev,
      [event.elementType]: event.complete,
    }));
    setEmpty((prev) => ({
      ...prev,
      [event.elementType]: event.empty,
    }));
  };

  useEffect(() => {
    if (
      !empty.cardNumber ||
      !empty.cardExpiry ||
      !empty.cardCvc ||
      cardName.trim()
    ) {
      checkIsCardEmpty?.(false);
    } else {
      checkIsCardEmpty?.(true);
    }
  }, [empty, cardName]);

  useEffect(() => {
    if (complete.cardNumber && complete.cardExpiry && complete.cardCvc) {
      onCardChange?.(true, cardName);
    } else {
      onCardChange?.(false, cardName);
    }
  }, [complete, onCardChange, cardName]);

  const handleReady = () => {
    if (onCardReady) {
      onCardReady();
    }
  };

  const handleFocus = () => {
    setFocused(true);
    if (onCardFocus) {
      onCardFocus();
    }
  };

  const handleBlur = () => {
    setFocused(false);
    if (onCardBlur) {
      onCardBlur();
    }
  };

  return (
    <div
      className={`stripe-card-element ${className} ${
        focused ? 'focused' : ''
      } ${error ? 'error' : ''}`}
    >
      <div className="card-form-background">
        <div className="card-form-container">
          <div className="grid grid--2 grid--sm-mob--1">
            <div className="form-group">
              <label className="field-label">Card Number *</label>
              <div className="stripe-input-container">
                <CardNumberElement
                  options={cardElementOptions}
                  onChange={handleChange}
                  onReady={handleReady}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                />
                {error.cardNumber && (
                  <span className="error-message">
                    <i className="cs-icon icon-error"></i>
                    {error.cardNumber}
                  </span>
                )}
              </div>
            </div>

            <div className="form-group">
              <label className="field-label">CVV *</label>
              <div className="stripe-input-container">
                <CardCvcElement
                  options={cardElementOptions}
                  onChange={handleChange}
                  onReady={handleReady}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                />
                {error.cardCvc && (
                  <span className="error-message">
                    <i className="cs-icon icon-error"></i>
                    {error.cardCvc}
                  </span>
                )}
              </div>
            </div>
            <div className="form-group">
              <label className="field-label">Name on Card</label>
              <div className="stripe-input-container">
                <input
                  type="text"
                  className="card-name-input"
                  placeholder="Enter cardholder name"
                  disabled={disabled}
                  value={cardName}
                  onChange={(e) => setCardName(e.target.value)}
                />
              </div>
            </div>

            <div className="form-group">
              <label className="field-label">Expiry Date *</label>
              <div className="stripe-input-container">
                <CardExpiryElement
                  options={cardElementOptions}
                  onChange={handleChange}
                  onReady={handleReady}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                />
                {error.cardExpiry && (
                  <span className="error-message">
                    <i className="cs-icon icon-error"></i>
                    {error.cardExpiry}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StripeCardElement;
